import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Avatar,
    Button,
    Tabs,
    Tab,
    Link as MuiLink,
    Grid,
    Divider,
    useTheme
} from '@mui/material';
import {
    PhoneIcon,
    EmailIcon,
    LanguageIcon,
    LocationOnIcon,
    SchoolIcon,
    WorkIcon,
    FacebookIcon,
    TwitterIcon,
    LinkedInIcon,
    InstagramIcon,
    MessageIcon,
    QrCodeIcon,
    OpenInNewIcon,
    WebsiteIcon,
    LocationIcon
} from 'icons/materialIcons';
import getIconForType from '../../utils/icons/getIconForType';
import placeholderImage from '../../images/placeholder-grey-image.jpg';
import avatarPlaceholder from '../../images/avatar-cropped.png';
import ProductsServicesPreviewCardDesign5 from '../links/productsServices/ProductsServicesPreview/ProductsServicesPreviewCardDesign5';
import QRCodeModal from '../common/QRCodeModal';
import { useEventTracking } from '../../services/eventTrackingService';
import LeadCaptureOverlay from '../leadCapture/LeadCaptureOverlay';
import { getContactLinkCategory } from '../../utils/getContactLinkCategory';
import { formatPhoneNumber, isPhoneContactType } from '../../utils/phoneUtils';

const CardDesign5FloatingButton = ({
    cardId,
    cardFullName,
    cardJobTitle,
    cardCompany,
    cardTheme,
    cardProfilePictureUrl,
    cardCoverPhotoUrl,
    cardCompanyLogoUrl,
    contextCardLayout,
    cardLinkColor,
    cardLocation,
    cardBio,
    cardPronoun,
    formattedPronoun,
    visibleContactLinks,
    previewContactLink,
    handleSaveContact,
    customLinkTypeLabels = {},
    showLeadCaptureAuto = false,
    }) => {
    const theme = useTheme();

    const tracking = useEventTracking();

    const [activeTab, setActiveTab] = useState(0);

    const [qrModalOpen, setQrModalOpen] = useState(false);

    const [showLeadCaptureButton, setShowLeadCaptureButton] = useState(false);

    const [useScrollableTabs, setUseScrollableTabs] = useState(false);

    const styles = theme.card.cardDesign5;
    const colors = styles.colors;
    const values = styles.values;
    const cssProps = styles.cssProperties;
    const fallbackText = styles.fallbackText;

    const primaryColor = cardTheme || colors.primary;
    const secondaryColor = cardLinkColor || colors.secondary;
    const profileCardColor = cardLinkColor || colors.profileCard;

    const socialLinks = useMemo(() => {
        const links = visibleContactLinks.filter(link => getContactLinkCategory(link.type) === 'Social Media');
        return links;
    }, [visibleContactLinks]);

    const productLinks = useMemo(() => {
        const links = visibleContactLinks.filter(link => {
            const category = getContactLinkCategory(link.type);
            const isProductLink = category === 'Products & Services';
            return isProductLink;
        });

        return links;
    }, [visibleContactLinks]);

    const contactLinks = useMemo(() => {
        const links = visibleContactLinks.filter(link => {
            const category = getContactLinkCategory(link.type);
            return category !== 'Social Media' && category !== 'Products & Services';
        });
        return links;
    }, [visibleContactLinks]);

    const getCustomLabel = useCallback((linkType) => {
        if (customLinkTypeLabels[linkType] !== undefined) {
            return customLinkTypeLabels[linkType];
        }

        switch (linkType) {
            case 'Social':
                return customLinkTypeLabels['Social'] !== undefined ? customLinkTypeLabels['Social'] : 'Social';
            case 'Products & Services':
                return customLinkTypeLabels['Products & Services'] !== undefined ? customLinkTypeLabels['Products & Services'] : 'Products & Services';
            case 'Contact':
            default:
                return customLinkTypeLabels['Contact'] !== undefined ? customLinkTypeLabels['Contact'] : 'Contact';
        }
    }, [customLinkTypeLabels]);

    const hasTabContent = useCallback((tabType) => {
        switch (tabType) {
            case 'contact':
                return contactLinks.length > 0 || cardJobTitle || cardCompany || cardLocation;
            case 'social':
                return socialLinks.length > 0;
            case 'products':
                return productLinks.length > 0;
            default:
                return false;
        }
    }, [contactLinks.length, socialLinks.length, productLinks.length, cardJobTitle, cardCompany, cardLocation]);

    const availableTabs = useMemo(() => {
        const tabs = [];

        if (hasTabContent('contact')) {
            tabs.push({
                type: 'contact',
                label: getCustomLabel('Contact'),
                index: tabs.length
            });
        }

        if (hasTabContent('social')) {
            tabs.push({
                type: 'social',
                label: getCustomLabel('Social'),
                index: tabs.length
            });
        }

        if (hasTabContent('products')) {
            tabs.push({
                type: 'products',
                label: getCustomLabel('Products & Services'),
                index: tabs.length
            });
        }

        return tabs;
    }, [hasTabContent, getCustomLabel]);

    const getDarkerShade = useCallback((color, amount = values.darkenAmount) => {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        const newR = Math.round(r * (1 - amount));
        const newG = Math.round(g * (1 - amount));
        const newB = Math.round(b * (1 - amount));

        return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }, [values.darkenAmount]);

    const getSaveButtonHoverColor = useCallback(() => {
        if (secondaryColor && secondaryColor !== primaryColor) {
            return secondaryColor;
        }
        return getDarkerShade(primaryColor, values.lightenAmount);
    }, [secondaryColor, primaryColor, getDarkerShade, values.lightenAmount]);

    const handleLinkClick = useCallback((link, linkType) => {
        if (link?.details?.formattedLink) {
            window.open(link.details.formattedLink, '_blank');
        } else if (link?.details?.linkUrl) {
            if (linkType === 'product' || linkType === 'service') {
                const url = link.details.linkUrl.startsWith('http')
                    ? link.details.linkUrl
                    : `https://${link.details.linkUrl}`;
                window.open(url, '_blank');
            } else {
                const url = link.details.linkUrl.startsWith('http') ?
                    link.details.linkUrl :
                    `https://${link.details.linkUrl}`;
                window.open(url, '_blank');
            }
        }

        if (tracking && link?.details && cardId) {
            try {
                const trackingData = {
                    linkType: linkType || 'unknown',
                    linkUrl: link.details.linkUrl || '',
                    linkTitle: link.details.linkTitle || linkType || 'untitled',
                    cardId: cardId
                };

                if (trackingData.cardId && trackingData.linkType) {
                    tracking.trackLinkTap(trackingData);
                }
            } catch (error) {
            }
        }
    }, [tracking, cardId]);

    // Helper function to get icon for link type using theme styles
    const getLinkIcon = useCallback((linkType) => {
        const type = (linkType || '').toLowerCase();
        switch (type) {
            case 'email':
                return <EmailIcon sx={styles.iconStyles} />;
            case 'phone':
            case 'call':
                return <PhoneIcon sx={styles.iconStyles} />;
            case 'website':
                return <WebsiteIcon sx={styles.iconStyles} />;
            case 'address':
                return <LocationIcon sx={styles.iconStyles} />;
            default:
                return React.cloneElement(getIconForType(linkType), {
                    sx: styles.iconStyles
                });
        }
    }, [styles.iconStyles]);

    const getSocialIcon = useCallback((linkType) => {
        const type = (linkType || '').toLowerCase();
        switch (type) {
            case 'facebook':
                return { icon: <FacebookIcon sx={styles.socialIconStyles} />, color: colors.facebook };
            case 'twitter':
            case 'x':
                return { icon: <TwitterIcon sx={styles.socialIconStyles} />, color: colors.twitter };
            case 'linkedin':
                return { icon: <LinkedInIcon sx={styles.socialIconStyles} />, color: colors.linkedin };
            case 'instagram':
                return { icon: <InstagramIcon sx={styles.socialIconStyles} />, color: colors.instagram };
            case 'wechat':
                return { icon: <MessageIcon sx={styles.socialIconStyles} />, color: colors.wechat };
            default:
                return {
                    icon: React.cloneElement(getIconForType(linkType), {
                        sx: styles.socialIconStyles
                    }),
                    color: secondaryColor || colors.secondary
                };
        }
    }, [styles.socialIconStyles, colors, secondaryColor]);

    const formatLinkLabel = useCallback((link) => {
        if (link?.details?.linkTitle) {
            return link.details.linkTitle.toUpperCase();
        }

        const type = (link?.type || '');
        switch (type.toLowerCase()) {
            case 'call':
                return fallbackText.phoneLabel;
            case 'x':
                return fallbackText.twitterLabel;
            case 'address':
                return fallbackText.addressLabel;
            default:
                return type.toUpperCase();
        }
    }, [fallbackText]);

    const hasValidAddressDisplay = useCallback((link) => {
        return link.details && (
            link.details.workAddress ||
            link.details.linkUrl ||
            link.details.coordinates
        );
    }, []);

    const getMapSource = useCallback((link) => {
        if (!link.details) return null;

        if (link.details.coordinates) {
            return `https://maps.googleapis.com/maps/api/staticmap?center=${link.details.coordinates.lat},${link.details.coordinates.lng}&zoom=15&size=600x300&maptype=roadmap&markers=color:red%7C${link.details.coordinates.lat},${link.details.coordinates.lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
        }

        const address = link.details.workAddress || link.details.linkUrl;
        if (address) {
            return `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(address)}&zoom=15&size=600x300&maptype=roadmap&markers=color:red%7C${encodeURIComponent(address)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
        }

        return null;
    }, []);

    const isAddressType = useCallback((link) => {
        return link.type?.toLowerCase() === 'address';
    }, []);

    const renderContactLinkItem = useCallback((link) => {
        const hasValidData = link?.details?.linkTitle || link?.details?.linkUrl;

        if (!hasValidData) {
            return null;
        }

        const isAddress = isAddressType(link);

        return (
            <Box key={link._id}>
                <Box
                    sx={isAddress ? styles.contactItemAddress : styles.contactItem}
                    onClick={() => handleLinkClick(link, link.type)}
                >
                    <Box sx={{
                        ...(isAddress ? styles.contactIconAddress : styles.contactIcon),
                        bgcolor: secondaryColor
                    }}>
                        {getLinkIcon(link.type)}
                    </Box>
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                        <Typography variant="caption" sx={styles.contactLabel}>
                            {formatLinkLabel(link)}
                        </Typography>
                        <Typography variant="body2" sx={styles.contactValue}>
                            {(() => {
                                if (isPhoneContactType(link.type) && link.details?.linkUrl) {
                                    return formatPhoneNumber(link.details.linkUrl, 'SG');
                                }
                                if (isAddress && link.details?.linkTitle && link.details.linkTitle !== fallbackText.addressLabel) {
                                    return link.details.linkUrl;
                                }
                                return link.details?.linkUrl || link.type;
                            })()}
                        </Typography>
                    </Box>
                </Box>
                {isAddress && hasValidAddressDisplay(link) && (
                    <Box
                        sx={{
                            ...styles.map.container,
                            mt: 1,
                            mb: 2,
                            backgroundColor: colors.greyBackground,
                            margin: { xs: "0.25rem 1.5rem", sm: "0.375rem 2rem", md: "0.5rem 2.5rem" },
                            borderRadius: "8px",
                            overflow: "hidden",
                            cursor: 'pointer',
                            position: 'relative'
                        }}
                        onClick={() => handleLinkClick(link, link.type)}
                    >
                        {(() => {
                            const mapSource = getMapSource(link);
                            if (mapSource) {
                                return (
                                    <>
                                        <img
                                            src={mapSource}
                                            alt="Location map"
                                            style={styles.map.image}
                                            onError={(e) => {
                                                const address = link.details.workAddress || link.details.linkUrl;
                                                if (address && !e.target.dataset.fallbackAttempted) {
                                                    e.target.dataset.fallbackAttempted = 'true';
                                                    e.target.src = `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(address)}&zoom=13&size=600x300&maptype=roadmap&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
                                                }
                                            }}
                                        />
                                        <Box sx={styles.map.overlay}>
                                            View larger map
                                        </Box>
                                    </>
                                );
                            } else {
                                return (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: colors.greyLight,
                                            color: colors.greyText
                                        }}
                                    >
                                        <Typography variant="body2">
                                            📍 {link.details.workAddress || link.details.linkUrl}
                                        </Typography>
                                    </Box>
                                );
                            }
                        })()}
                    </Box>
                )}
            </Box>
        );
    }, [styles, colors, secondaryColor, fallbackText, isAddressType, handleLinkClick, getLinkIcon, formatLinkLabel, hasValidAddressDisplay, getMapSource]);

    const renderSocialLinkItem = useCallback((link) => {
        const hasValidData = link?.details?.linkTitle || link?.details?.linkUrl;

        if (!hasValidData) {
            return null;
        }

        const { icon, color } = getSocialIcon(link.type);
        return (
            <Box
                key={link._id}
                sx={styles.contactItem}
                onClick={() => handleLinkClick(link, link.type)}
            >
                <Box sx={{
                    ...styles.contactIconLarge,
                    bgcolor: color
                }}>
                    {link.details?.icon ? (
                        <Box
                            sx={{
                                ...styles.socialIconImage,
                                backgroundImage: `url(${link.details.icon})`
                            }}
                        />
                    ) : icon}
                </Box>
                <Box sx={{ minWidth: 0, width: '100%' }}>
                    <Typography variant="caption" sx={styles.contactLabel}>
                        {formatLinkLabel(link)}
                    </Typography>
                    <Typography variant="body2" sx={styles.contactValue}>
                        {(() => {
                            if (isPhoneContactType(link.type) && link.details?.linkUrl) {
                                return formatPhoneNumber(link.details.linkUrl, 'SG');
                            }
                            return link.details?.linkTitle || link.details?.linkUrl || link.type;
                        })()}
                    </Typography>
                </Box>
            </Box>
        );
    }, [styles, getSocialIcon, handleLinkClick, formatLinkLabel]);

    const renderContactTab = useCallback(() => {
        return (
            <Box sx={styles.contactTabContainer}>
                {(cardJobTitle || cardCompany) && (
                    <>
                        <Typography variant="subtitle1" sx={styles.sectionTitle}>
                            ABOUT
                        </Typography>

                        {cardJobTitle && (
                            <Box sx={styles.contactItem}>
                                <Box sx={{
                                    ...styles.contactIcon,
                                    bgcolor: secondaryColor
                                }}>
                                    <WorkIcon sx={styles.iconStyles} />
                                </Box>
                                <Box sx={{ minWidth: 0, width: '100%' }}>
                                    <Typography variant="caption" sx={styles.contactLabel}>
                                        {fallbackText.jobTitleLabel}
                                    </Typography>
                                    <Typography variant="body2" sx={styles.contactValue}>
                                        {cardJobTitle}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {cardCompany && (
                            <Box sx={styles.contactItem}>
                                <Box sx={{
                                    ...styles.contactIcon,
                                    bgcolor: secondaryColor
                                }}>
                                    <WorkIcon sx={styles.iconStyles} />
                                </Box>
                                <Box sx={{ minWidth: 0, width: '100%' }}>
                                    <Typography variant="caption" sx={styles.contactLabel}>
                                        {fallbackText.companyNameLabel}
                                    </Typography>
                                    <Typography variant="body2" sx={styles.contactValue}>
                                        {cardCompany}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </>
                )}

                {contactLinks.length > 0 && (
                    <>
                        <Typography variant="subtitle1" sx={styles.sectionTitle}>
                            {fallbackText.contactSectionTitle}
                        </Typography>
                        {contactLinks.map((link, index) => (
                            <Box key={link.linkId || link.uuid || index}>
                                {renderContactLinkItem(link)}
                            </Box>
                        ))}
                    </>
                )}

                {cardLocation && !contactLinks.find(link => link.type?.toLowerCase() === 'address') && (
                    <Box sx={styles.contactItem}>
                        <Box sx={{
                            ...styles.contactIcon,
                            bgcolor: secondaryColor
                        }}>
                            <LocationIcon sx={styles.iconStyles} />
                        </Box>
                        <Box sx={{ minWidth: 0, width: '100%' }}>
                            <Typography variant="caption" sx={styles.contactLabel}>
                                {fallbackText.addressLabel}
                            </Typography>
                            <Typography variant="body2" sx={styles.contactValue}>
                                {cardLocation}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {!cardJobTitle && !cardCompany && contactLinks.length === 0 && !cardLocation && (
                    <Box sx={styles.emptyMessage}>
                        <Typography variant="body2" sx={styles.emptyMessage}>
                            No contact information available.
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    }, [contactLinks, cardJobTitle, cardCompany, cardLocation, styles, secondaryColor, fallbackText, renderContactLinkItem]);

    const renderSocialTab = useCallback(() => {
        return (
            <Box sx={styles.contactTabContainer}>
                {socialLinks.map((link, index) => (
                    <Box key={link.linkId || link.uuid || index}>
                        {renderSocialLinkItem(link)}
                    </Box>
                ))}

                {socialLinks.length === 0 && (
                    <Box sx={styles.emptyMessage}>
                        <Typography variant="body2" sx={styles.emptyMessage}>
                            {fallbackText.noSocialLinks}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    }, [socialLinks, styles, fallbackText, renderSocialLinkItem]);

    const renderProductsTab = useCallback(() => {
        return (
            <Box sx={styles.productSection}>
                {productLinks && productLinks.length > 0 ? (
                    <Box sx={styles.productSectionContent}>
                        {productLinks.map((productLink, index) => (
                            <Box key={productLink._id || index} sx={styles.productItem}>
                                <ProductsServicesPreviewCardDesign5
                                    link={productLink}
                                    cardLinkColor={primaryColor}
                                    isFullDisplay={false}
                                />
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={styles.emptyMessage}>
                        <Typography variant="body2" sx={styles.emptyMessage}>
                            {fallbackText.noProducts}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    }, [productLinks, styles, fallbackText, primaryColor]);

    // NOW the tabContent useMemo can safely reference these functions
    const tabContent = useMemo(() => {
        const tab = availableTabs[activeTab];
        if (!tab) return null;

        switch (tab.type) {
            case 'contact':
                return renderContactTab();
            case 'social':
                return renderSocialTab();
            case 'products':
                return renderProductsTab();
            default:
                return null;
        }
    }, [activeTab, availableTabs, renderContactTab, renderSocialTab, renderProductsTab]);

    useEffect(() => {
    }, [showLeadCaptureAuto]);

    useEffect(() => {
    }, [visibleContactLinks?.length, JSON.stringify(visibleContactLinks?.map(link => link.id))]);

    useEffect(() => {
    }, []);

    useEffect(() => {
        const needsScrollable = availableTabs.length > 3 ||
            availableTabs.some(tab => tab.label.length > 12);

        setUseScrollableTabs(needsScrollable);
    }, [
        availableTabs.length,
        JSON.stringify(availableTabs.map(tab => tab.label))
    ]);

    useEffect(() => {
        if (availableTabs.length > 0 && activeTab >= availableTabs.length) {
            setActiveTab(0);
        }
    }, [availableTabs.length, activeTab]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const shouldShowTabs = () => {
        return true;
    };

    const handleConnect = () => {
        setShowLeadCaptureButton(true);
    };

    const handleLeadCaptureButtonClose = () => {
        setShowLeadCaptureButton(false);
    };

    const handleLeadCaptureButtonSuccess = () => {
        setShowLeadCaptureButton(false);
    };

    return (
        <Box sx={styles.mainContainer}>
            <Box
                sx={{
                    ...styles.headerBackground,
                    bgcolor: (cardCoverPhotoUrl && cardCoverPhotoUrl !== placeholderImage) ? cssProps.transparent : primaryColor,
                    backgroundImage: (cardCoverPhotoUrl && cardCoverPhotoUrl !== placeholderImage) ? `url(${cardCoverPhotoUrl})` : cssProps.none,
                    backgroundSize: cssProps.backgroundSize,
                    backgroundPosition: cssProps.backgroundPosition,
                    backgroundRepeat: cssProps.backgroundRepeat,
                }}
            >
                <Box sx={styles.headerContent}>
                    {(cardCompanyLogoUrl || cardCompany) ? (
                        cardCompanyLogoUrl ? (
                            <Box sx={styles.companyLogo}>
                                <img
                                    src={cardCompanyLogoUrl}
                                    alt={fallbackText.companyLogoAlt}
                                    style={styles.avatarImageStyles}
                                />
                            </Box>
                        ) : (
                            <Typography
                                component="span"
                                sx={styles.companyInitial}
                            >
                                {cardCompany.charAt(0).toUpperCase()}
                            </Typography>
                        )
                    ) : (
                        <Box sx={{ width: { xs: "2.5rem", sm: "2.8125rem", md: "3.125rem" } }} />
                    )}

                    <Box
                        onClick={() => setQrModalOpen(true)}
                        sx={{
                            ...styles.qrIcon,
                            ...styles.qrIconHover
                        }}
                    >
                        <QrCodeIcon sx={styles.qrIconInner} />
                    </Box>
                </Box>
            </Box>

            <Box sx={{
                ...styles.whiteBackground,
                paddingBottom: { xs: "6rem", sm: "6.5rem", md: "7rem" },
            }}>
                <Box sx={styles.profileCard}>
                    <Box
                        sx={{
                            ...styles.profilePhotoSection,
                            backgroundImage: `url(${cardProfilePictureUrl || avatarPlaceholder})`
                        }}
                    >
                        {(!cardProfilePictureUrl && !avatarPlaceholder) && (
                            <Avatar
                                src={avatarPlaceholder}
                                sx={styles.fallbackAvatar}
                            />
                        )}
                    </Box>

                    <Box
                        sx={{
                            ...styles.profileCardRightSection,
                            bgcolor: primaryColor,
                            color: colors.white
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={styles.profileCardName}
                        >
                            {cardFullName || fallbackText.fullName}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                ...styles.profileCardJobTitle,
                                opacity: values.profileCardOpacity
                            }}
                        >
                            {cardJobTitle}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{
                    paddingTop: cardBio
                        ? { xs: "2rem", sm: "2.5rem", md: "3rem" }
                        : { xs: "1.5rem", sm: "1.5rem", md: "2rem" },
                    paddingBottom: 1,
                    textAlign: "center",
                }}>
                    {cardBio && (
                        <Typography variant="body2" sx={styles.secondaryJobTitleText}>
                            {cardBio}
                        </Typography>
                    )}
                </Box>

                {shouldShowTabs() && (
                    <Box sx={styles.tabsContainer}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            variant={useScrollableTabs ? "scrollable" : "fullWidth"}
                            scrollButtons={useScrollableTabs ? "auto" : false}
                            allowScrollButtonsMobile={useScrollableTabs}
                            sx={{
                                ...styles.tabs,
                                ...(useScrollableTabs && {
                                    "& .MuiTabs-scrollButtons": {
                                        color: primaryColor,
                                        opacity: 0.7,
                                        "&.Mui-disabled": {
                                            opacity: 0.3,
                                        },
                                        "&:hover": {
                                            opacity: 1,
                                            backgroundColor: "rgba(0,0,0,0.04)",
                                        }
                                    },
                                    "& .MuiTabs-flexContainer": {
                                        gap: { xs: "0.25rem", sm: "0.5rem" },
                                        paddingX: 1,
                                    },
                                    "& .MuiTabs-scroller": {
                                        scrollBehavior: "smooth"
                                    }
                                })
                            }}
                        >
                            {availableTabs.map((tab, index) => (
                                <Tab
                                    key={tab.type}
                                    label={tab.label}
                                    sx={{
                                        ...(useScrollableTabs ? {
                                            minWidth: "auto",
                                            flexGrow: 0,
                                            flexShrink: 0,
                                            paddingX: { xs: "0.75rem", sm: "1rem", md: "1.25rem" },
                                            whiteSpace: "nowrap",
                                            textOverflow: "clip",
                                            overflow: "visible"
                                        } : {
                                            ...(tab.type === 'products' ? styles.tabItemLarge : styles.tabItem)
                                        })
                                    }}
                                />
                            ))}
                        </Tabs>
                    </Box>
                )}

                {shouldShowTabs() ? (
                    <Box sx={styles.tabContent}>
                        {tabContent}
                    </Box>
                ) : (
                    <Box sx={styles.contactTabContainer}>
                        {(cardJobTitle || cardCompany) && (
                            <>
                                <Typography variant="subtitle1" sx={styles.sectionTitle}>
                                    ABOUT
                                </Typography>

                                {cardJobTitle && (
                                    <Box sx={styles.contactItem}>
                                        <Box sx={{
                                            ...styles.contactIcon,
                                            bgcolor: secondaryColor
                                        }}>
                                            <WorkIcon sx={styles.iconStyles} />
                                        </Box>
                                        <Box sx={{ minWidth: 0, width: '100%' }}>
                                            <Typography variant="caption" sx={styles.contactLabel}>
                                                {fallbackText.jobTitleLabel}
                                            </Typography>
                                            <Typography variant="body2" sx={styles.contactValue}>
                                                {cardJobTitle}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {cardCompany && (
                                    <Box sx={styles.contactItem}>
                                        <Box sx={{
                                            ...styles.contactIcon,
                                            bgcolor: secondaryColor
                                        }}>
                                            <WorkIcon sx={styles.iconStyles} />
                                        </Box>
                                        <Box sx={{ minWidth: 0, width: '100%' }}>
                                            <Typography variant="caption" sx={styles.contactLabel}>
                                                {fallbackText.companyNameLabel}
                                            </Typography>
                                            <Typography variant="body2" sx={styles.contactValue}>
                                                {cardCompany}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                )}
            </Box>

            <Box sx={{
                position: 'fixed',
                bottom: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                left: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                right: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                display: 'flex',
                justifyContent: 'center',
                zIndex: 1000,
                pointerEvents: 'none',
            }}>
                <Box sx={{
                    display: "flex",
                    gap: { xs: 2, sm: 2.5, md: 3 },
                    maxWidth: { xs: "320px", sm: "360px", md: "400px" },
                    width: '100%',
                    padding: { xs: "0 1rem", sm: "0 1.25rem", md: "0 1.5rem" },
                    pointerEvents: 'auto',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <Button
                        variant="contained"
                        onClick={handleSaveContact}
                        sx={{
                            flex: 2.5,
                            backgroundColor: primaryColor,
                            borderRadius: "8px",
                            paddingY: { xs: 1.5, sm: 1.75, md: 2 },
                            color: "#fff",
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                                backgroundColor: getSaveButtonHoverColor(),
                                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2), 0 3px 6px rgba(0, 0, 0, 0.15)',
                                transform: 'translateY(-1px)',
                            },
                            '&:active': {
                                transform: 'translateY(0px)',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 1px 3px rgba(0, 0, 0, 0.15)',
                            }
                        }}
                    >
                        {fallbackText.saveContactButton}
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleConnect}
                        sx={{
                            flex: 2.5,
                            backgroundColor: secondaryColor,
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            paddingY: { xs: 1.5, sm: 1.75, md: 2 },
                            color: "#fff",
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                                backgroundColor: getSaveButtonHoverColor(),
                                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2), 0 3px 6px rgba(0, 0, 0, 0.15)',
                                transform: 'translateY(-1px)',
                            },
                            '&:active': {
                                transform: 'translateY(0px)',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 1px 3px rgba(0, 0, 0, 0.15)',
                            }
                        }}
                    >
                        {fallbackText.connectButton}
                    </Button>
                </Box>
            </Box>

            <QRCodeModal
                open={qrModalOpen}
                onClose={() => setQrModalOpen(false)}
                cardId={cardId}
            />

            {showLeadCaptureAuto && (
                <LeadCaptureOverlay
                    onClose={() => { }}
                    onSuccess={() => { }}
                    cardLayoutType={contextCardLayout}
                    addMoreConnectButtonColor={secondaryColor}
                    triggerSource="auto"
                />
            )}

            {showLeadCaptureButton && (
                <LeadCaptureOverlay
                    onClose={handleLeadCaptureButtonClose}
                    onSuccess={handleLeadCaptureButtonSuccess}
                    cardLayoutType={contextCardLayout}
                    addMoreConnectButtonColor={secondaryColor}
                    triggerSource="button"
                />
            )}
        </Box>
    );
};

export default CardDesign5FloatingButton;